import {
  ActionFunction,
  LoaderFunction,
  useLoaderData,
} from "react-router-dom";

import { Models } from "@/api/types";
import {
  applyPromoCode,
  applyRepeatCash,
  applyReward,
  CartContainer,
  CartPageAction,
  deleteSalesAssociate,
  MEMBERSHIP_BENEFIT_KEY,
  ondeleteCartItem,
  ondeletePromoCode,
  onEditCartItem,
  setSalesAssociate,
  updateLocation,
} from "@/modules/cart";
import { CURRENT_ORDER_QUERY_KEY } from "@/modules/cart/useCartDetails";
import { get } from "api";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { readFormData } from "toolbox/ReadFormData";

const load = async (request: Request) => {
  const platform = queryClient.fetchQuery(["platform"], () => get("/platform"));
  const salesAssociates = queryClient.fetchQuery(
    ["platform-location/sales-associates"],
    () => get("/platform-location/sales-associates")
  );
  const membershipDiscount = queryClient.fetchQuery({
    queryKey: ["membership/details"],
    queryFn: () => get("/orders/current/membership/details"),
  });
  const cart = await queryClient
    .ensureQueryData({
      queryKey: CURRENT_ORDER_QUERY_KEY,
      queryFn: () => get("/orders/current/details"),
      staleTime: 5 * 60 * 1000,
    })
    .catch(() => null);

  const lineItems = queryClient.fetchQuery({
    queryKey: ["/orders/current/lineitems"],
    queryFn: () => get("/orders/current/lineitems"),
    staleTime: 5 * 60 * 1000,
  });

  const locations = queryClient.fetchQuery({
    queryKey: ["platform/locations"],
    queryFn: async () => {
      const response = await get("/platform/locations", {});
      return response.items as Models<"GetPlatformLocationsResult">[];
    },
  });

  /* When displaying the page in a mobile webview, we let the app handle displaying its own navbar */
  const showNavbar =
    new URL(request.url).searchParams.get("isMobileApp") !== "true";

  const listings = queryClient.fetchQuery(["/listings"], () =>
    get("/listings")
  );

  // Clear the membership benefit from localStorage when entering Cart for the first time
  localStorage.removeItem(MEMBERSHIP_BENEFIT_KEY);

  return waitObj({
    platform,
    salesAssociates,
    showNavbar,
    membershipDiscount,
    cart,
    lineItems,
    listings,
    locations,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const cartLoader: LoaderFunction = ({ request }) => {
  return load(request);
};

export const cartPageAction: ActionFunction = async ({ request }) => {
  const data = await readFormData<CartPageAction>(request);
  if (data.action === "applyCash") {
    return applyRepeatCash(data.amount);
  } else if (data.action === "deleteItem") {
    return ondeleteCartItem(data);
  } else if (data.action === "editItem") {
    return onEditCartItem(data);
  } else if (data.action === "promoCodeAction") {
    return applyPromoCode(data);
  } else if (data.action === "removePromoCodeAction") {
    return ondeletePromoCode(data);
  } else if (data.action === "setSalesAssociate") {
    return setSalesAssociate(data);
  } else if (data.action === "deleteSalesAssociate") {
    return deleteSalesAssociate();
  } else if (data.action === "rewardAction") {
    return applyReward(data);
  } else if (data.action === "updateLocation") {
    return updateLocation(data);
  } else {
    throw Error("Should be unreachable");
  }
};

const CartPage = () => {
  const {
    platform,
    salesAssociates,
    showNavbar,
    membershipDiscount,
    locations,
    cart,
    lineItems,
    listings,
  } = useLoaderData() as LoaderData;

  return (
    <div className="CartPage">
      <CartContainer
        platform={platform}
        salesAssociates={salesAssociates}
        showNavbar={showNavbar}
        membershipDiscount={membershipDiscount}
        cart={cart as Models<"ShopDisplayOrderResult">}
        initialLineItems={lineItems}
        listings={listings}
        initialLocations={locations}
      />
    </div>
  );
};

export default CartPage;
